const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

function getAuthHeaders() {
  const token = localStorage.getItem('accessToken');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

export interface WaterLog {
  id: string;
  userId: string;
  amount: number;
  loggedAt: string;
  notes?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface WaterGoal {
  id: string;
  userId: string;
  dailyGoalMl: number;
  reminderEnabled: boolean;
  reminderIntervalHours: number;
  createdAt: string;
  updatedAt: string;
}

export interface DailySummary {
  date: string;
  totalMl: number;
  goalMl: number;
  percentage: number;
  remainingMl: number;
  logs: WaterLog[];
  goal: WaterGoal;
}

export interface WeeklyDay {
  date: string;
  totalMl: number;
  goalMl: number;
  percentage: number;
}

export interface WeeklyStats {
  days: WeeklyDay[];
  summary: {
    avgDailyMl: number;
    daysGoalMet: number;
    goalMl: number;
  };
}

const waterService = {
  // Get today's summary (or a specific date)
  async getTodaySummary(date?: string): Promise<DailySummary> {
    const url = date
      ? `${API_URL}/api/water/today?date=${date}`
      : `${API_URL}/api/water/today`;
    const res = await fetch(url, { headers: getAuthHeaders() });
    if (!res.ok) throw new Error('Eroare la încărcarea datelor');
    return res.json();
  },

  // Add water intake
  async addWater(amount: number, notes?: string): Promise<{ log: WaterLog; summary: DailySummary; message: string }> {
    const res = await fetch(`${API_URL}/api/water/add`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ amount, notes }),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || 'Eroare la adăugarea apei');
    }
    return res.json();
  },

  // Delete a water log entry
  async deleteLog(id: string): Promise<{ success: boolean; summary: DailySummary }> {
    const res = await fetch(`${API_URL}/api/water/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || 'Eroare la ștergerea înregistrării');
    }
    return res.json();
  },

  // Get current goal
  async getGoal(): Promise<WaterGoal> {
    const res = await fetch(`${API_URL}/api/water/goal`, { headers: getAuthHeaders() });
    if (!res.ok) throw new Error('Eroare la încărcarea obiectivului');
    return res.json();
  },

  // Update daily goal
  async updateGoal(dailyGoalMl: number, reminderEnabled?: boolean, reminderIntervalHours?: number): Promise<{ goal: WaterGoal; message: string }> {
    const res = await fetch(`${API_URL}/api/water/goal`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ dailyGoalMl, reminderEnabled, reminderIntervalHours }),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || 'Eroare la actualizarea obiectivului');
    }
    return res.json();
  },

  // Get weekly stats
  async getWeeklyStats(): Promise<WeeklyStats> {
    const res = await fetch(`${API_URL}/api/water/weekly`, { headers: getAuthHeaders() });
    if (!res.ok) throw new Error('Eroare la încărcarea statisticilor');
    return res.json();
  },

  // Get logs with pagination
  async getLogs(params?: { date?: string; page?: number; limit?: number }) {
    const query = new URLSearchParams();
    if (params?.date) query.set('date', params.date);
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    const res = await fetch(`${API_URL}/api/water/logs?${query}`, { headers: getAuthHeaders() });
    if (!res.ok) throw new Error('Eroare la încărcarea logurilor');
    return res.json();
  },
};

export default waterService;
